/**
 * NotificationRouter
 *
 * Bridges the service worker's `notification-navigate` message into
 * React Router. When a user taps a push notification on their phone
 * (installed PWA) and the AkountFly PWA is already running, the SW
 * posts a message to the client and we route via `useNavigate` instead
 * of relying on SW-side `client.navigate` (which is unreliable on iOS
 * standalone PWAs).
 *
 * Renders nothing; purely a side-effect component.
 */
import { useEffect } from 'react';
import { rememberRedirect } from '../utils/postLoginRedirect';
import { useNavigate } from 'react-router-dom';

export default function NotificationRouter() {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'notification-navigate' || !data.url) return;
      // React Router navigate handles internal paths; external URLs fall
      // back to window.location for safety.
      try {
        if (typeof data.url === 'string' && data.url.startsWith('/')) {
          rememberRedirect(data.url); // survives a sign-in if the session lapsed
          navigate(data.url);
        } else {
          window.location.href = data.url;
        }
      } catch {
        window.location.href = data.url;
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return null;
}
